import {
  RestoreNotActivatedError,
  SignalActionForbiddenError,
  type FeedSignal,
} from "./types";

/**
 * Pure restore logic (R-4), kept out of the component so both rules can be
 * asserted directly and reused if a second surface ever needs them.
 */

/**
 * Visibility rule for the Restore control: owner-or-admin on an archived signal.
 *
 * This mirrors the server exactly. restore-signal calls requireAssignedSignal,
 * which resolves to canUseAssignedSignal — admin OR assignee OR claimer
 * (supabase/functions/_shared/signal-actions.ts:223-226) — a check the function
 * gained in commit ab01f612. Showing the control to anyone else would offer an
 * action the server answers with a 403.
 */
export function canRestoreSignal(
  signal: Pick<FeedSignal, "lifecycleStatus" | "claimedBy" | "assignedTo">,
  viewerId: string,
  isAdmin: boolean,
): boolean {
  if (signal.lifecycleStatus !== "archived") return false;
  if (isAdmin) return true;
  return signal.claimedBy === viewerId || signal.assignedTo === viewerId;
}

/**
 * Copy for a failed restore. The server distinguishes two cases worth naming —
 * 403 forbidden and 409 not-archived — and everything else is an honest retry.
 * No raw server strings reach the user.
 */
export function restoreErrorMessage(error: unknown): string {
  if (error instanceof SignalActionForbiddenError) {
    return "You don't have permission to restore this signal.";
  }
  if (error instanceof RestoreNotActivatedError) {
    return "Restore isn't enabled yet — it turns on once the server update ships.";
  }
  if (/not archived/i.test(error instanceof Error ? error.message : "")) {
    return "This signal is already active — reopen it to see the latest.";
  }
  return "Couldn't restore — try again.";
}
